/**
 * Quote Update Request Service
 * Handles quote update request API operations using shared API client
 */

import api from "@/shared/api";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Accept a quote update request
 * @param {string} quoteId - Quote ID
 * @param {string} requestId - Update request ID
 * @param {Object} payload - Acceptance payload
 * @param {string} payload.priceAction - "keep" or "update"
 * @param {number} [payload.adjustedQuoteValue] - New price if priceAction is "update"
 * @param {string} [payload.resolutionNotes] - Optional notes
 * @returns {Promise<Object>} Result
 */
export const acceptQuoteUpdateRequest = async (
  quoteId,
  requestId,
  payload = {}
) => {
  try {
    const response = await api.post(
      `/quotes/${quoteId}/update-requests/${requestId}/accept`,
      payload
    );
    logInfo("Quote update request accepted", { quoteId, requestId });
    return response;
  } catch (error) {
    logError("Failed to accept quote update request", {
      quoteId,
      requestId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to accept update request",
    };
  }
};

/**
 * Reject a quote update request
 * @param {string} quoteId - Quote ID
 * @param {string} requestId - Update request ID
 * @param {Object} payload - Rejection payload
 * @param {string} [payload.resolutionNotes] - Optional rejection notes
 * @returns {Promise<Object>} Result
 */
export const rejectQuoteUpdateRequest = async (
  quoteId,
  requestId,
  payload = {}
) => {
  try {
    const response = await api.post(
      `/quotes/${quoteId}/update-requests/${requestId}/reject`,
      payload
    );
    logInfo("Quote update request rejected", { quoteId, requestId });
    return response;
  } catch (error) {
    logError("Failed to reject quote update request", {
      quoteId,
      requestId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to reject update request",
    };
  }
};
